import { Helmet } from "react-helmet-async"
import { serializeJsonLd } from "@/lib/seo/structuredData"

export type SEOOgType = "website" | "article" | "event"

export interface SEOHeadProps {
  title: string
  description: string
  canonicalUrl: string
  ogType: SEOOgType
  imageUrl?: string | null
  /** Single or multiple JSON-LD objects */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null
  /** Preview / staging pages should not be indexed */
  robotsNoindex?: boolean
}

export function SEOHead({
  title,
  description,
  canonicalUrl,
  ogType,
  imageUrl,
  jsonLd,
  robotsNoindex = false,
}: SEOHeadProps) {
  const desc = description.trim() || title
  const ld = jsonLd
    ? Array.isArray(jsonLd)
      ? jsonLd
      : [jsonLd]
    : []

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={canonicalUrl} />
      {robotsNoindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      {imageUrl ? <meta property="og:image" content={imageUrl} /> : null}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      {imageUrl ? <meta name="twitter:image" content={imageUrl} /> : null}

      {/* NOS-242: serializeJsonLd escapes <, >, & so user content (titles
          etc.) can never terminate the script tag (script injection). */}
      {ld.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {serializeJsonLd(obj)}
        </script>
      ))}
    </Helmet>
  )
}
